import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { formatPrice } from '../data/products';
import { useCart } from '../context/CartContext';
import { useEffect, useMemo, useState } from 'react';
import { getColorHex } from '../utils/colors';
import { resolveProductImage, getProductImageSrcSet } from '../utils/productUtils';
import VariantBottomSheet from './VariantBottomSheet';

export default function ProductCard({ product, index }: any) {
  const { addItem } = useCart();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Normalize data from API or static source
  const variants = useMemo(() => product.product_variants || product.variants || [], [product]);
  const firstVariant = variants[0];
  const shortName = product.short_name || product.shortName || '';
  const thumbnail = resolveProductImage(product.thumbnail_url || firstVariant?.image_url || firstVariant?.image);

  const hasPromo = useMemo(() => variants.some((v: any) => v.promo_price || v.promoPrice), [variants]);
  const colors = useMemo<string[]>(
    () => [...new Set(variants.map((v: any) => v.color).filter(Boolean) as string[])],
    [variants]
  );
  const hasMultipleVariants = variants.length > 1;
  const isSoldOut = useMemo(() => variants.length > 0 && variants.every((v: any) => (v.stock ?? 0) === 0), [variants]);

  const [selectedColor, setSelectedColor] = useState<string>('');
  const [selectedOption, setSelectedOption] = useState<string>('');

  useEffect(() => {
    if (!isPickerOpen) return;
    if (!colors.length) return;
    if (!selectedColor) setSelectedColor(colors[0]);
  }, [colors, isPickerOpen, selectedColor]);

  const colorVariants = useMemo(
    () => variants.filter((v: any) => v.color === selectedColor),
    [variants, selectedColor]
  );

  const options = useMemo<string[]>(
    () =>
      [...new Set(colorVariants.map((v: any) => v.option_name || v.option).filter(Boolean) as string[])],
    [colorVariants]
  );

  useEffect(() => {
    if (!isPickerOpen) return;
    if (!options.length) return;
    if (!selectedOption) setSelectedOption(options[0]);
  }, [isPickerOpen, options, selectedOption]);

  const activeVariant = useMemo(() => {
    if (!isPickerOpen) return firstVariant;
    return (
      colorVariants.find((v: any) => (v.option_name || v.option) === selectedOption) ||
      colorVariants[0] ||
      firstVariant
    );
  }, [colorVariants, firstVariant, isPickerOpen, selectedOption]);



  const commitAdd = (variant: any) => {
    if (!variant) return;
    const normalizedColor = String(variant.color || '').trim().toLowerCase();
    const groupForColor = (product.variant_groups || []).find(
      (g: any) => String(g.color || '').trim().toLowerCase() === normalizedColor
    );
    const groupFirstImage = (groupForColor?.images || [])
      .map((img: any) => (typeof img === 'string' ? img : img?.image_url))
      .find(Boolean);
    const variantImage = groupFirstImage || variant.image_url || variant.image || product.thumbnail_url || product.thumbnailUrl;

    addItem({
      sku: variant.sku,
      productId: product.id,
      productName: shortName,
      color: variant.color,
      option: variant.option_name || variant.option,
      price: variant.price,
      promoPrice: variant.promo_price || variant.promoPrice,
      quantity: 1,
      image: resolveProductImage(variantImage),
      stockLimit: variant.stock,
    });
  };

  const handleQuickAdd = () => {
    if (!firstVariant) return;
    if (hasMultipleVariants) {
      setIsPickerOpen(true);
      return;
    }
    commitAdd(firstVariant);
  };

  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 35 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-50px' }}
        transition={{ duration: 1, delay: index * 0.1, ease: [0.25, 1, 0.5, 1] }}
        className="flex flex-col group"
      >
        {/* Image */}
        <Link
          to={product.slug ? `/koleksi/${product.slug}` : `/product/${product.id}`}
          className="relative aspect-[3/4] w-full overflow-hidden bg-gray-100 mb-4 block"
        >
          {(!imageLoaded || !thumbnail) && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse" />
          )}
          {hasPromo && !isSoldOut && (
            <div className="absolute top-4 left-4 z-10 bg-white/95 text-brand-wine text-label px-3 py-1.5 border border-brand-wine/10">
              Promo
            </div>
          )}
          {isSoldOut && (
            <div className="absolute top-4 left-4 z-10 bg-black/80 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">
              Habis Terjual
            </div>
          )}
          <img
            src={thumbnail || 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='}
            srcSet={getProductImageSrcSet(thumbnail)}
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            alt={shortName}
            onLoad={() => setImageLoaded(true)}
            className={`w-full h-full object-cover transition-transform duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110 ${isSoldOut ? 'grayscale contrast-75 brightness-75' : 'grayscale-[10%] group-hover:grayscale-0'} ${imageLoaded && thumbnail ? 'opacity-100' : 'opacity-0'}`}
            referrerPolicy="no-referrer"
            loading="lazy"
          />

          {/* Quick Add overlay: always visible on touch, hover-reveal on desktop */}
          <div className="absolute inset-x-0 bottom-0 p-3 opacity-100 md:opacity-0 md:translate-y-2 md:group-hover:opacity-100 md:group-hover:translate-y-0 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]">
            <button
              onClick={(e) => {
                e.preventDefault();
                if (isSoldOut) return;
                handleQuickAdd();
              }}
              disabled={isSoldOut}
              className={`w-full text-label py-3 flex items-center justify-center gap-2 border border-brand-wine/10 group/btn active:scale-[0.98] transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${isSoldOut ? 'bg-black/40 backdrop-blur-sm text-white cursor-not-allowed' : 'bg-white/95 hover:bg-brand-wine hover:text-white hover:border-brand-wine text-brand-wine'}`}
              aria-label={isSoldOut ? 'Habis Terjual' : (hasMultipleVariants ? 'Pilih varian lalu tambah ke keranjang' : 'Tambah ke keranjang')}
            >
              <ShoppingBag className="w-4 h-4 transition-transform duration-300 group-hover/btn:-translate-y-0.5" />
              {isSoldOut ? 'Habis Terjual' : (hasMultipleVariants ? 'Pilih Varian' : 'Tambah ke Keranjang')}
            </button>
          </div>
        </Link>

        {/* Info */}
        <Link to={product.slug ? `/koleksi/${product.slug}` : `/product/${product.id}`} className="flex flex-col flex-1">
          <div className="flex flex-wrap gap-2 items-center mb-1">
            <span className="text-[9px] uppercase tracking-[0.12em] font-normal text-muted/65">
              {product.category}
            </span>
            {product.tags && Array.isArray(product.tags) && product.tags.length > 0 && (
              <div className="flex gap-1.5 items-center">
                <span className="w-[1px] h-2.5 bg-muted/15" />
                <div className="flex gap-1.5 overflow-hidden">
                  {product.tags.slice(0, 2).map((tag: string) => (
                    <span key={tag} className="text-[9px] font-normal text-muted/55 uppercase tracking-[0.08em]">
                      {tag}
                    </span>
                  ))}
                  {product.tags.length > 2 && (
                    <span className="text-[9px] text-muted/20">+</span>
                  )}
                </div>
              </div>
            )}
          </div>
          <h3 className="text-base md:text-lg font-serif text-ink tracking-wide mb-1 group-hover:text-brand-wine transition-colors duration-300 leading-snug">
            {shortName}
          </h3>
          <div className="flex items-baseline gap-2 mb-3">
            {hasPromo && (
              <span className="text-xs text-muted/70 line-through font-medium">
                {formatPrice(firstVariant.price)}
              </span>
            )}
            <span className="text-sm md:text-base font-medium text-ink">
              {formatPrice((hasPromo ? (firstVariant.promo_price || firstVariant.promoPrice) : firstVariant?.price) ?? 0)}
            </span>
          </div>

          {/* Color dots */}
          <div className="flex items-center gap-1.5 mt-auto">
            {colors.slice(0, 5).map((color: any) => {
              const customHex = (product.variant_groups || []).find(
                (g: any) => g.color?.toLowerCase() === color?.toLowerCase()
              )?.hex_code;
              return (
                <span
                  key={color}
                  className="w-3 h-3 border border-brand-wine/15 cursor-pointer transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:scale-125 hover:border-brand-wine hover:z-10"
                  style={{ backgroundColor: customHex || getColorHex(color) }}
                  role="img"
                  aria-label={`Warna ${color}`}
                />
              );
            })}
            {colors.length > 5 && (
              <span className="text-[10px] text-muted/85">+{colors.length - 5}</span>
            )}
          </div>
        </Link>
      </motion.div>

      <VariantBottomSheet
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        product={product}
        variants={variants}
        colors={colors}
        selectedColor={selectedColor}
        setSelectedColor={setSelectedColor}
        options={options}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        activeVariant={activeVariant}
        onAdd={() => {
          commitAdd(activeVariant);
          setIsPickerOpen(false);
        }}
        shortName={shortName}
      />
    </>
  );
}
